import {ErrorHandler, Injectable, Injector} from '@angular/core';
import * as Sentry from "@sentry/angular";
import {environment} from "@src/environments/environment";
import {isBrowser} from './is-browser';
import {SwUpdate} from '@angular/service-worker';

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {

  private static CHUNK_RELOAD_KEY = 'chunk_reload_timestamp';
  // Comfortably longer than REFRESH_TIMEOUT_MS so a reload that actually had
  // to wait out the timeout still lands outside the cooldown window.
  private static CHUNK_RELOAD_COOLDOWN_MS = 45000; // 45 seconds
  // Upper bound on how long we wait for checkForUpdate()/activateUpdate()
  // before reloading anyway — an unresponsive service worker must never be
  // able to suppress the reload that recovers from a broken navigation.
  private static REFRESH_TIMEOUT_MS = 5000; // 5 seconds

  // Resolved lazily via Injector rather than constructor-injected: this
  // ErrorHandler is itself instantiated *during* ApplicationRef's own
  // construction (ApplicationRef -> INTERNAL_APPLICATION_ERROR_HANDLER ->
  // ErrorHandler). Constructor-injecting SwUpdate here pulls in
  // NgswCommChannel, whose constructor injects ApplicationRef back —
  // a cyclic dependency (NG0200) since ApplicationRef isn't done constructing
  // yet. Deferring the lookup until handleError() actually runs sidesteps it,
  // since by then ApplicationRef is long finished.
  constructor(private injector: Injector) {
  }

  handleError(error: Error): void {
    if (error) {
      console.error('AB ERROR:', error);
    }

    if (environment.production) {
      Sentry.captureException(error);
    }

    const chunkFailedMessage = /Loading chunk [\d]+ failed/;

    if (chunkFailedMessage.test(error?.message)) {
      if (isBrowser()) {
        const lastReload = Number(sessionStorage.getItem(GlobalErrorHandler.CHUNK_RELOAD_KEY) || '0');
        const now = Date.now();

        if (now - lastReload < GlobalErrorHandler.CHUNK_RELOAD_COOLDOWN_MS) {
          console.error('[ChunkError] Reload loop detected, skipping reload. Last reload was', Math.round((now - lastReload) / 1000), 'seconds ago');
          return;
        }

        console.error('[ChunkError] Chunk loading failed, reloading window:', error?.message);
        sessionStorage.setItem(GlobalErrorHandler.CHUNK_RELOAD_KEY, String(now));
        this.refreshStaleAppShell();
      }
    }
  }

  /**
   * A missing chunk means the app shell we're running is stale — a plain
   * reload can just re-serve the exact same cached shell from the service
   * worker and hit the same missing chunk again. Fetch and activate whatever
   * new version is available first, so the reload actually lands on a shell
   * whose chunk manifest matches what's on the server.
   */
  private refreshStaleAppShell(): void {
    const swUpdate = this.injector.get(SwUpdate);

    if (!swUpdate.isEnabled) {
      window.location.reload();
      return;
    }

    const refresh = swUpdate.checkForUpdate()
      .then(() => swUpdate.activateUpdate())
      .catch((err) => Sentry.captureException(err));

    const timeout = new Promise((resolve) => setTimeout(resolve, GlobalErrorHandler.REFRESH_TIMEOUT_MS));

    // An unresponsive service worker must not block recovery from a broken
    // navigation — reload once whichever settles first.
    Promise.race([refresh, timeout]).finally(() => window.location.reload());
  }
}
